import type { TFunction } from 'i18next';
import type { Sample } from '../services/sample.service';
import { escapeHtml } from './escapeHtml';
import {
  buildCaseManagerHref,
  caseLocationCellHtmlDataAttrs,
  formatSampleStorageLabels,
  sampleToCaseLocationCell,
  type CaseLocationCell,
} from './caseLocationUtils';
import { joinLabeledValue } from './joinLabeledValue';
import { softBreakIdentifier } from './softBreakIdentifier';

export function isUnassignedStorageCell(cell: CaseLocationCell): boolean {
  return cell.storageRoomId == null;
}

/** ケース導線の accessible name（joinLabeledValue と同型） */
export function formatCaseManagerOpenLabel(openVerb: string, target: string): string {
  return joinLabeledValue(openVerb, target);
}

/** 一覧／詳細で共有するケース導線の長短ラベル */
export function resolveCaseManagerOpenVerbs(
  t: TFunction,
  unassigned: boolean
): { full: string; short: string } {
  const fullAssigned = t('sample.case_manager.open_in_case_manager');
  const shortAssigned = t('sample.case_manager.open_in_case_manager_short', {
    defaultValue: fullAssigned,
  });
  if (!unassigned) {
    return { full: fullAssigned, short: shortAssigned };
  }
  const full = t('sample.case_manager.open_unassigned_in_case_manager', {
    defaultValue: fullAssigned,
  });
  const short = t('sample.case_manager.open_unassigned_in_case_manager_short', {
    defaultValue: shortAssigned,
  });
  return { full, short };
}

/** 一覧の保管庫セル HTML（未設定は警告スタイル＋設定導線の aria） */
export function renderSampleStorageLinkHtml(row: Sample, t: TFunction): string {
  const cell = sampleToCaseLocationCell(row);
  const href = buildCaseManagerHref();
  const dataAttrs = caseLocationCellHtmlDataAttrs(cell);
  const room =
    row.storageRoom && typeof row.storageRoom === 'object'
      ? (row.storageRoom as { name?: string; location?: string | null })
      : row.storageRoom ?? null;
  const { compact, roomLine, detailLine } = formatSampleStorageLabels(cell, room, t);
  const unassigned = isUnassignedStorageCell(cell);
  const { full: openVerbFull, short: openVerbShort } = resolveCaseManagerOpenVerbs(
    t,
    unassigned
  );
  // 一覧セルは room · #n · case を見せる。title は詳しい動詞、aria は短名
  const title = escapeHtml(formatCaseManagerOpenLabel(openVerbFull, compact));
  const aria = escapeHtml(formatCaseManagerOpenLabel(openVerbShort, compact));
  const detailHtml = detailLine
    ? `<span class="sample-storage-detail sample-ellipsis-show-tail"><span class="sample-ellipsis-show-tail-inner">${escapeHtml(softBreakIdentifier(detailLine))}</span></span>`
    : '';
  const className = [
    'text-decoration-none',
    'case-manager-nav-link',
    'sample-storage-link',
    unassigned ? 'sample-storage-link--unassigned' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return `<a href="${href}" class="${className}" ${dataAttrs} title="${title}" aria-label="${aria}"><span class="sample-storage-compact"><span class="sample-storage-room">${escapeHtml(softBreakIdentifier(roomLine))}</span>${detailHtml}</span></a>`;
}
